
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AdminSubmission {
  id: string;
  business_type: string;
  employee_count: string;
  monthly_energy_costs: number;
  email: string | null;
  company_name: string | null;
  energy_potential_score: number | null;
  yearly_annual_savings: number | null;
  co2_reduction: number | null;
  roi_months: number | null;
  created_at: string;
  current_solutions: string[];
  energy_peak_hours: number[];
  expansion_plans: string[];
  priorities: Record<string, number>;
  recommendations: string[];
  expansion_details: Record<string, any>;
}

export const useAdminData = () => {
  const [submissions, setSubmissions] = useState<AdminSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      
      // Fetch main submissions
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('quickscan_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (submissionsError) throw submissionsError;

      // Fetch related data for each submission
      const enrichedSubmissions = await Promise.all(
        submissionsData.map(async (submission) => {
          const [
            { data: solutions },
            { data: peakHours },
            { data: expansionPlans },
            { data: priorities },
            { data: recommendations },
            { data: expansionDetails }
          ] = await Promise.all([
            supabase.from('quickscan_current_solutions').select('solution').eq('submission_id', submission.id),
            supabase.from('quickscan_energy_peak_hours').select('peak_hour').eq('submission_id', submission.id),
            supabase.from('quickscan_expansion_plans').select('plan_type').eq('submission_id', submission.id),
            supabase.from('quickscan_priorities').select('priority_key, priority_value').eq('submission_id', submission.id),
            supabase.from('quickscan_recommendations').select('recommendation').eq('submission_id', submission.id),
            supabase.from('quickscan_expansion_details').select('plan_type, details').eq('submission_id', submission.id)
          ]);

          return {
            ...submission,
            current_solutions: solutions?.map(s => s.solution) || [],
            energy_peak_hours: peakHours?.map(p => p.peak_hour) || [],
            expansion_plans: expansionPlans?.map(p => p.plan_type) || [],
            priorities: priorities?.reduce((acc, p) => ({ ...acc, [p.priority_key]: p.priority_value }), {}) || {},
            recommendations: recommendations?.map(r => r.recommendation) || [],
            expansion_details: expansionDetails?.reduce((acc, d) => ({ ...acc, [d.plan_type]: d.details }), {}) || {}
          };
        })
      );

      setSubmissions(enrichedSubmissions);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast({
        title: 'Fout bij laden data',
        description: 'Er ging iets mis bij het ophalen van de submissions.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteSubmission = async (id: string) => {
    try {
      const { error } = await supabase
        .from('quickscan_submissions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSubmissions(prev => prev.filter(s => s.id !== id));
      toast({
        title: 'Submission verwijderd',
        description: 'De submission is succesvol verwijderd.',
      });
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast({
        title: 'Fout bij verwijderen',
        description: 'Er ging iets mis bij het verwijderen van de submission.',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  return {
    submissions,
    loading,
    refetch: fetchSubmissions,
    deleteSubmission
  };
};
